import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormControl, FormArray, FormGroup, AbstractControl, FormBuilder } from '@angular/forms';
import { Observable,combineLatest, throwError, merge, EMPTY } from 'rxjs';
import { map, distinct, mapTo, catchError, retry, switchMap, take } from 'rxjs/operators';
import { Product, ProductTpl, Packaging, ProductPackagingVal } from '@app/shared/_models/product';
import { Event, Partner, PartnerLinksForm } from '@app/shared/_models/common';
import { ProductAttribute } from '@app/shared/_models/product';
// import { Attribute } from '@app/shared/_models/product';
import { SpinnerService } from '@app/shared/spinner.service';
import { CommonService } from '@app/common/common.service';
import { LoginService } from '@app/common/login.service';
import * as constants from "@app/shared/constants";
import * as common from '@app/shared/_models/common';

import { environment } from '@app/../environments/environment';

const headers = { 'content-type': 'application/json' };

var forDevData = require('./moc_partner.json')

@Injectable({
  providedIn: 'root'
})
export class PartnerService {

  constructor(public loginService: LoginService, private http: HttpClient , private commonService: CommonService ) { }
  
  getPartnerAsFormGroup(id:number) : Observable<FormGroup> {
		return this.getPartnerWithForm$(id).pipe(map((r) => r.formGroup));
	  }

  // Le tiers TEL QUE LE SERVEUR LE REND, et son formulaire. La fiche tiers garde le premier
  // comme état persisté : c'est lui qu'elle rend à la commande à sa fermeture — jamais la
  // saisie en cours, qui porte des lignes DELETE et des adresses neuves à l'id négatif.
  getPartnerWithForm$(id:number) : Observable<{ partner: Partner; formGroup: FormGroup }> {
		return combineLatest ([this.getPartnerByid(id), this.commonService.getCufs( constants.C_COMPONENT_ADDRESS), this.commonService.getCufs( constants.C_COMPONENT_ADDRESS_GEO), this.commonService.getCufs( constants.C_COMPONENT_PARTNER) ]  ) .pipe(map( ([partners, addressCufs, geoCufs, partnerCufs] : [ Partner[], common.Cuf[], common.Cuf[], common.Cuf[] ]) =>{
			// 2e argument : structure adresse + géo — les controls cufs Latitude/Longitude existent toujours dans data.cufs des adresses.
			// 3e argument : structure du TIERS (composant 2) — `data` devient un FormGroup et data.cufs est éditable.
			return { partner: partners[0], formGroup: Partner.asFormGroup(partners[0], addressCufs.concat(geoCufs), partnerCufs ) };
		}));
	  }
	  
  getPartnerByid(id:number) : Observable<any> {
	  return this.http.get<void>(environment.server_url + "/common/partner/" + id );
  }    
  
  // Les lignes RATTACHÉES au tiers, chacune avec les rôles qu'elle y tient (c_partner_partner).
  //
  // Ce que getPartnerByid ne dit pas : il rend les adresses (`addresses`), c'est-à-dire l'EFFET
  // du rôle -5, jamais la relation elle-même. Cette route-ci rend la table c_partner_partner
  // telle qu'elle est — une ligne par (rattaché, rôle) : {parent, child, role, is_default, data},
  // l'objet lié chargé du côté demandé (`lnk_child_partner` en direct, `lnk_parent_partner` avec dir=in), `role`
  // étant un id de c_values 26. Regrouper par rattaché est l'affaire de l'écran.
  //
  // APPEL SÉPARÉ ET NON UNE OPTION DE LA FICHE : seule la vue « Rattachements » en a besoin, et
  // l'ouverture d'un tiers n'a pas à payer une jointure de plus à chaque fois.
  // dir : 'out' (défaut) = les tiers rattachés à celui-ci ; 'in' = ceux auprès de qui il l'est.
  getPartnerLinks(id:number, dir: 'in'|'out' = 'out') : Observable<any> {
	  return this.http.get<any>(environment.server_url + "/common/partner/" + id + "/links" + (dir === 'in' ? "?dir=in" : ""));
  }

  // Écrit des RELATIONS (c_partner_partner) : chaque entrée porte son action — INSERT, UPDATE, DELETE.
  // Ce qui n'est pas dans la liste n'est pas touché ; c'est un delta, pas un état complet.
  //
  // POURQUOI RECHARGER LE TIERS ENTIER AVANT DE POSTER : l'event PARTNER n'est pas un patch. Son
  // handler fait un `UPDATE c_partner SET (typ, civility, …) = (…)` avec CE QU'IL REÇOIT — poster
  // un objet réduit à {id, lnk_partner_partners} mettrait toutes les autres colonnes à NULL. On repart donc de
  // l'état frais du serveur, on y greffe les liens, et on renvoie le tout. Même motif que
  // saveAddressCoords$ ci-dessus.
  savePartnerLinks$(partnerId: number, liens: any[]): Observable<any> {
	  // take(1) : getPartnerAsFormGroup ne complète jamais (il vit sur un BehaviorSubject).
	  return this.getPartnerAsFormGroup(partnerId).pipe(take(1), switchMap((fg: FormGroup) => {
		  fg.setControl('lnk_partner_partners', new FormControl(liens));
		  return this.postPartnerEvent(constants.EVENTTYP_UPDATE, fg);
	  }));
  }
  
  // Écrit lat/lng (cufs Latitude/Longitude) sur UNE adresse du tiers et poste l'event PARTNER UPDATE.
  // Le partner est rechargé frais ; seule la ligne d'adresse ciblée passe en action UPDATE
  // (les autres restent UNCHANGED — rien côté back). Utilisé par la programmation de livraison.
  saveAddressCoords$(partnerId: number, addressId: number, lat: number, lon: number): Observable<any> {
	  // take(1) : getPartnerAsFormGroup ne complète jamais (getValues$ vit sur un BehaviorSubject) —
	  // sans lui, un ValuesService.updateData() ultérieur rejouerait le POST avec des coordonnées périmées.
	  return this.getPartnerAsFormGroup(partnerId).pipe(take(1), switchMap((fg: FormGroup) => {
		  // L'adresse est la ligne de rôle -5 de lnk_partner_partners dont l'enfant porte cet id ;
		  // c'est cette ligne qui passe en UPDATE, avec l'enfant modifié dans lnk_child_partner.
		  const line = PartnerLinksForm.addressLines(fg).find((l) => +(PartnerLinksForm.addressOf(l).get('id')?.value) === +addressId);
		  if (!line) return throwError(() => new Error('Adresse ' + addressId + ' introuvable sur le tiers ' + partnerId));
		  const cufs = PartnerLinksForm.addressOf(line).get('data')?.get('cufs') as FormGroup | null;
		  if (!cufs) return throwError(() => new Error('Adresse ' + addressId + ' sans groupe cufs'));
		  // setControl (crée OU remplace) : écrit même si la structure géo (c_values 12, composant 5) manque
		  // en base — un ?.setValue sur un control absent échouerait SILENCIEUSEMENT (coords perdues sans erreur).
		  cufs.setControl('' + constants.CUF_ADDRESS_LAT, new FormControl(lat));
		  cufs.setControl('' + constants.CUF_ADDRESS_LNG, new FormControl(lon));
		  line.get('action')?.setValue('UPDATE');
		  return this.postPartnerEvent(constants.EVENTTYP_UPDATE, fg);
	  }));
  }

  postPartnerEvent(eventtyp: number, formGroup: FormGroup): Observable<any> {
	  let event:Event = {
		  id: 0,
		  typ: eventtyp,			// 1: Insert, 2: Update
			userid: this.loginService.loggeduser?.id,
		  objectid: 0, 
		  objecttyp: constants.ObjectType.PARTNER,		// Partner
		  data: formGroup.value,
	  }
	  return this.http.post<any>(environment.server_url + "/event", event, { 'headers': headers });
  }
}
